// Enhanced connections tab with sophisticated design patterns and improved visual hierarchy
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  CheckCircle,
  Linkedin,
  X,
  Facebook,
  Instagram,
  Youtube,
  Globe,
  PenSquare,
  Rss,
  Clock,
  Bot,
  Loader2,
  Link as LinkIcon,
  Calendar,
  Settings
} from 'lucide-react';
import usePlatformSettings from '../hooks/usePlatformSettings';
import { useTimezone } from '@/hooks/useTimezone';

interface ConnectionsTabProps {
  /** Brand ID to fetch all platform settings for */
  brandId: string;
}

// Custom TikTok icon component
const TikTokIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
  </svg>
);

// Function to get the appropriate icon based on platform key
const getPlatformIcon = (platformKey: string, size: 'sm' | 'md' | 'lg' = 'md') => {
  const sizeClass = size === 'sm' ? 'h-4 w-4' : size === 'lg' ? 'h-8 w-8' : 'h-5 w-5';
  
  switch(platformKey?.toLowerCase()) {
    case 'linkedin':
    case 'linkedin_article':
      return <Linkedin className={sizeClass} />;
    case 'twitter':
      return <X className={sizeClass} />;
    case 'facebook':
      return <Facebook className={sizeClass} />;
    case 'instagram':
      return <Instagram className={sizeClass} />;
    case 'youtube':
      return <Youtube className={sizeClass} />;
    case 'tiktok':
      return <TikTokIcon className={sizeClass} />;
    case 'wordpress':
      return <Globe className={sizeClass} />;
    case 'medium':
      return <PenSquare className={sizeClass} />;
    case 'rss':
      return <Rss className={sizeClass} />;
    default:
      return <Globe className={sizeClass} />;
  }
};




const ConnectionsTab: React.FC<ConnectionsTabProps> = ({ brandId }) => {
  const navigate = useNavigate();
  const { formatTime, getAbbreviation } = useTimezone();

  const {
    platforms,
    platformSettings,
    loading
  } = usePlatformSettings(brandId);

  // Get platforms that have platform_brand_settings records and are enabled, organized by Auto-Create status
  const { autoCreateEnabledPlatforms, autoCreateDisabledPlatforms } = useMemo(() => {
    // Only show platforms that have a platform_brand_settings record AND are enabled
    const platformsWithSettings = platforms.filter(platform =>
      platformSettings[platform.id] && platform.enabled === true
    );

    const autoCreateEnabled = platformsWithSettings.filter(platform =>
      platformSettings[platform.id]?.autogenerate_enabled === true
    );

    const autoCreateDisabled = platformsWithSettings.filter(platform =>
      platformSettings[platform.id]?.autogenerate_enabled === false
    );

    return {
      autoCreateEnabledPlatforms: autoCreateEnabled,
      autoCreateDisabledPlatforms: autoCreateDisabled
    };
  }, [platforms, platformSettings]);



  const handlePlatformSettings = (platform: any) => {
    navigate(`/brands/${brandId}/connections/${platform.id}/settings`);
  };

  // Helper function to format days of week
  const formatDaysOfWeek = (days: string[] | null | undefined): string => {
    if (!days || days.length === 0) return 'Not set';

    const dayMap: Record<string, string> = {
      'MONDAY': 'Mon',
      'TUESDAY': 'Tue',
      'WEDNESDAY': 'Wed',
      'THURSDAY': 'Thu',
      'FRIDAY': 'Fri',
      'SATURDAY': 'Sat',
      'SUNDAY': 'Sun'
    };

    return days.map(day => dayMap[day] || day).join(', ');
  };

  // Helper function to format time with timezone
  const formatTimeWithTimezone = (time: string | null | undefined): string => {
    if (!time) return 'Not set';

    try {
      // Parse the UTC time string (format: "HH:MM" or "HH:MM:SS")
      const [hours, minutes] = time.split(':').map(Number);

      // Create a UTC date with the given time
      const utcDate = new Date();
      utcDate.setUTCHours(hours, minutes, 0, 0);

      // Format to user's local timezone
      const localTime = formatTime(utcDate, false); // Use 12-hour format
      const tzAbbr = getAbbreviation();

      return `${localTime} ${tzAbbr}`;
    } catch (error) {
      console.error('Error formatting time:', error);
      return time;
    }
  };

  // Function to extract user profile information from connection details
  const getUserProfileInfo = (platformKey: string, details: Record<string, any>) => {
    if (!details) return null;
    
    switch (platformKey) {
      case 'linkedin':
        // For LinkedIn, prioritize organization name if posting as organization
        if (details.post_as_organization && details.organization_id) {
          const organizations = details.organizations || [];
          const selectedOrg = organizations.find((org: any) => org.id === details.organization_id);
          if (selectedOrg?.name) return selectedOrg.name;
        }
        // Fall back to personal profile information
        if (details.name) return details.name;
        if (details.localizedFirstName && details.localizedLastName) {
          return `${details.localizedFirstName} ${details.localizedLastName}`;
        }
        if (details.username) return details.username;
        return null;
      
      case 'twitter':
        if (details.username) return `@${details.username}`;
        if (details.name) return details.name;
        return null;
      
      case 'facebook':
        // For Facebook, prioritize selected page name over user profile
        if (details.selected_page_name) return details.selected_page_name;
        // Fall back to user profile information
        if (details.user_name) return details.user_name;
        if (details.name) return details.name;
        if (details.username) return details.username;
        return null;
      
      case 'instagram':
        if (details.instagram_username) return `@${details.instagram_username}`;
        if (details.username) return `@${details.username}`;
        if (details.name) return details.name;
        return null;
        
      case 'wordpress':
        if (details.site_url) return details.site_url;
        if (details.username) return details.username;
        return null;
        
      case 'medium':
        if (details.username) return `@${details.username}`;
        if (details.name) return details.name;
        return null;
        
      case 'linkedin_article':
        if (details.name) return details.name;
        if (details.localizedFirstName && details.localizedLastName) {
          return `${details.localizedFirstName} ${details.localizedLastName}`;
        }
        if (details.username) return details.username;
        return null;
      
      default:
        if (details.username) return details.username;
        if (details.name) return details.name;
        return null;
    }
  };


  return (
    <div className="space-y-6">

      {/* Connected Accounts Section */}
      <div>

        {loading ? (
          <div className="space-y-4">
            {/* Enhanced loading skeletons */}
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="relative overflow-hidden bg-gradient-to-r from-blue-50/50 via-indigo-50/30 to-purple-50/20 rounded-xl border border-gray-200/50 p-4 sm:p-6">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse" />
                <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
                  <div className="h-12 w-12 sm:h-16 sm:w-16 bg-gradient-to-br from-gray-200 to-gray-300 rounded-xl animate-pulse flex-shrink-0" />
                  <div className="flex-1 space-y-3 w-full sm:w-auto">
                    <div className="h-5 sm:h-6 bg-gradient-to-r from-gray-200 to-gray-300 rounded-lg w-full sm:w-48 animate-pulse" />
                    <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-full animate-pulse" />
                  </div>
                  <div className="h-10 w-full sm:w-32 bg-gradient-to-r from-gray-200 to-gray-300 rounded-lg animate-pulse flex-shrink-0" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            {/* All Platforms Section - Full-width cards */}
            {(autoCreateEnabledPlatforms.length > 0 || autoCreateDisabledPlatforms.length > 0) && (
              <div className="space-y-4">
                {/* Auto-Create Enabled Platforms */}
                {autoCreateEnabledPlatforms.map((platform) => {
                  const displayName = platform.platform_key === 'twitter' ? 'X' :
                    platform.platform_key === 'linkedin_article' ? 'LinkedIn Articles' :
                      platform.name;

                  const settings = platformSettings[platform.id] || {};
                  const connectionDetails = settings.connection_details || {};
                  const username = getUserProfileInfo(platform.platform_key, connectionDetails);

                  // Get scheduling information
                  const preferredDays = formatDaysOfWeek(settings.preferred_days);
                  const preferredTime = formatTimeWithTimezone(settings.preferred_time);

                  return (
                    <Card
                      key={platform.id}
                      className="group relative overflow-hidden transition-all duration-300 hover:shadow-lg cursor-pointer border-emerald-200/60 bg-gradient-to-br from-emerald-50/30 to-green-50/20 hover:border-emerald-300/60"
                      onClick={() => handlePlatformSettings(platform)}
                    >
                      {/* Animated background overlay */}
                      <div className="absolute inset-0 bg-gradient-to-r from-emerald-50/20 via-transparent to-green-50/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                      <CardContent className="relative p-4 sm:p-6">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 sm:gap-6">
                          {/* Left side: Platform icon, name, and status */}
                          <div className="flex items-start gap-3 sm:gap-4 flex-1 min-w-0">
                            {/* Enhanced platform icon with gradient background */}
                            <div className="p-2 sm:p-3 rounded-xl bg-gradient-to-br from-white to-gray-100/80 shadow-sm border border-gray-200/50 group-hover:shadow-md group-hover:scale-110 transition-all duration-300 flex-shrink-0">
                              {getPlatformIcon(platform.platform_key, 'lg')}
                            </div>

                            {/* Platform info */}
                            <div className="flex-1 min-w-0">
                              {/* Title and badges stack on mobile */}
                              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-3">
                                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">{displayName}</h3>

                                {/* Status badges - wrap on mobile */}
                                <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                                  <div className="flex items-center gap-1.5 px-2 sm:px-2.5 py-1 bg-emerald-50/80 text-emerald-700 border border-emerald-200/60 rounded-full text-xs font-medium whitespace-nowrap">
                                    <Bot className="h-3.5 w-3.5 flex-shrink-0" />
                                    Auto-Create
                                  </div>
                                  {settings.status === 'CONNECTED' ? (
                                    <div className="flex items-center gap-1.5 px-2 sm:px-2.5 py-1 bg-blue-50/80 text-blue-700 border border-blue-200/60 rounded-full text-xs font-medium whitespace-nowrap">
                                      <CheckCircle className="h-3.5 w-3.5 flex-shrink-0" />
                                      Connected
                                    </div>
                                  ) : settings.status === 'PENDING' ? (
                                    <div className="flex items-center gap-1.5 px-2 sm:px-2.5 py-1 bg-amber-50/80 text-amber-700 border border-amber-200/60 rounded-full text-xs font-medium whitespace-nowrap">
                                      <Loader2 className="h-3.5 w-3.5 animate-spin flex-shrink-0" />
                                      Connecting...
                                    </div>
                                  ) : settings.status === 'DISCONNECTED' ? (
                                    <div className="flex items-center gap-1.5 px-2 sm:px-2.5 py-1 bg-red-50/80 text-red-700 border border-red-200/60 rounded-full text-xs font-medium whitespace-nowrap">
                                      <X className="h-3.5 w-3.5 flex-shrink-0" />
                                      Disconnected
                                    </div>
                                  ) : null}
                                  {settings.autopost_enabled ? (
                                    <div className="flex items-center gap-1.5 px-2 sm:px-2.5 py-1 bg-indigo-50/80 text-indigo-700 border border-indigo-200/60 rounded-full text-xs font-medium whitespace-nowrap">
                                      <Clock className="h-3.5 w-3.5 flex-shrink-0" />
                                      Auto-Publish
                                    </div>
                                  ) : (
                                    <div className="flex items-center gap-1.5 px-2 sm:px-2.5 py-1 bg-gray-100 text-gray-600 border border-gray-300 rounded-full text-xs font-medium whitespace-nowrap">
                                      <Clock className="h-3.5 w-3.5 flex-shrink-0" />
                                      No Auto-Publish
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Two-column layout for scheduling info */}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mt-4">
                                {/* Column 1: Connected Profile */}
                                <div className="flex items-start gap-2">
                                  <div className="p-1.5 sm:p-2 rounded-lg bg-blue-50/80 flex-shrink-0">
                                    <LinkIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-blue-600" />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Connected Profile</div>
                                    <div className="text-sm font-semibold text-gray-900 break-words">
                                      {username || 'Not connected'}
                                    </div>
                                  </div>
                                </div>

                                {/* Column 2: Auto-Create Schedule & Auto-Publish Time */}
                                <div className="flex items-start gap-2">
                                  <div className="p-1.5 sm:p-2 rounded-lg bg-purple-50/80 flex-shrink-0">
                                    <Calendar className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-purple-600" />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Auto-Create For</div>
                                    <div className="text-sm font-semibold text-gray-900 break-words">
                                      {preferredDays}
                                    </div>
                                    {settings.autopost_enabled && (
                                      <div className="text-xs text-gray-600 mt-1 break-words">
                                        Publishes at {preferredTime}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Right side: Configure button - full width on mobile */}
                          <div className="flex-shrink-0 w-full sm:w-auto">
                            <Button
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePlatformSettings(platform);
                              }}
                              variant="outline"
                              className="w-full sm:w-auto gap-2 bg-white hover:bg-gray-50 border-gray-300 hover:border-gray-400"
                            >
                              <Settings className="h-4 w-4" />
                              Configure
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}

                {/* Auto-Create Disabled Platforms */}
                {autoCreateDisabledPlatforms.map((platform) => {
                  const displayName = platform.platform_key === 'twitter' ? 'X' :
                    platform.platform_key === 'linkedin_article' ? 'LinkedIn Articles' :
                      platform.name;

                  const settings = platformSettings[platform.id] || {};
                  const connectionDetails = settings.connection_details || {};
                  const username = getUserProfileInfo(platform.platform_key, connectionDetails);

                  return (
                    <Card
                      key={platform.id}
                      className="group relative overflow-hidden transition-all duration-300 hover:shadow-lg cursor-pointer border-gray-300 bg-gradient-to-br from-gray-50/30 to-slate-50/20 hover:border-gray-400"
                      onClick={() => handlePlatformSettings(platform)}
                    >
                      {/* Animated background overlay */}
                      <div className="absolute inset-0 bg-gradient-to-r from-gray-50/20 via-transparent to-slate-50/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                      <CardContent className="relative p-4 sm:p-6">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 sm:gap-6">
                          {/* Left side: Platform icon, name, and status */}
                          <div className="flex items-start gap-3 sm:gap-4 flex-1 min-w-0">
                            {/* Enhanced platform icon with gradient background */}
                            <div className="p-2 sm:p-3 rounded-xl bg-gradient-to-br from-white to-gray-100/80 shadow-sm border border-gray-200/50 group-hover:shadow-md group-hover:scale-110 transition-all duration-300 flex-shrink-0">
                              {getPlatformIcon(platform.platform_key, 'lg')}
                            </div>

                            {/* Platform info */}
                            <div className="flex-1 min-w-0">
                              {/* Title and badges stack on mobile */}
                              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-3">
                                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">{displayName}</h3>

                                {/* Status badges - wrap on mobile */}
                                <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                                  <div className="flex items-center gap-1.5 px-2 sm:px-2.5 py-1 bg-gray-100 text-gray-600 border border-gray-300 rounded-full text-xs font-medium whitespace-nowrap">
                                    <Bot className="h-3.5 w-3.5 flex-shrink-0" />
                                    Auto-Create Disabled
                                  </div>
                                  {settings.status === 'CONNECTED' ? (
                                    <div className="flex items-center gap-1.5 px-2 sm:px-2.5 py-1 bg-blue-50/80 text-blue-700 border border-blue-200/60 rounded-full text-xs font-medium whitespace-nowrap">
                                      <CheckCircle className="h-3.5 w-3.5 flex-shrink-0" />
                                      Connected
                                    </div>
                                  ) : settings.status === 'PENDING' ? (
                                    <div className="flex items-center gap-1.5 px-2 sm:px-2.5 py-1 bg-amber-50/80 text-amber-700 border border-amber-200/60 rounded-full text-xs font-medium whitespace-nowrap">
                                      <Loader2 className="h-3.5 w-3.5 animate-spin flex-shrink-0" />
                                      Connecting...
                                    </div>
                                  ) : settings.status === 'DISCONNECTED' ? (
                                    <div className="flex items-center gap-1.5 px-2 sm:px-2.5 py-1 bg-red-50/80 text-red-700 border border-red-200/60 rounded-full text-xs font-medium whitespace-nowrap">
                                      <X className="h-3.5 w-3.5 flex-shrink-0" />
                                      Disconnected
                                    </div>
                                  ) : null}
                                  <div className="flex items-center gap-1.5 px-2 sm:px-2.5 py-1 bg-gray-100 text-gray-600 border border-gray-300 rounded-full text-xs font-medium whitespace-nowrap">
                                    <Clock className="h-3.5 w-3.5 flex-shrink-0" />
                                    No Auto-Publish
                                  </div>
                                </div>
                              </div>

                              {/* Three-column layout for scheduling info - Grayed out for disabled */}
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 mt-4 opacity-50">
                                {/* Column 1: Connected Profile */}
                                <div className="flex items-start gap-2">
                                  <div className="p-1.5 sm:p-2 rounded-lg bg-gray-100 flex-shrink-0">
                                    <LinkIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-500" />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Connected Profile</div>
                                    <div className="text-sm font-semibold text-gray-700 break-words">
                                      {username || 'Not connected'}
                                    </div>
                                  </div>
                                </div>

                                {/* Column 2: Auto-Create Schedule - Disabled */}
                                <div className="flex items-start gap-2">
                                  <div className="p-1.5 sm:p-2 rounded-lg bg-gray-100 flex-shrink-0">
                                    <Calendar className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-500" />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Auto-Create For</div>
                                    <div className="text-sm font-semibold text-gray-700 break-words">
                                      Disabled
                                    </div>
                                  </div>
                                </div>

                                {/* Column 3: Auto-Publish Time - Disabled */}
                                <div className="flex items-start gap-2">
                                  <div className="p-1.5 sm:p-2 rounded-lg bg-gray-100 flex-shrink-0">
                                    <Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-500" />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Auto-Publish</div>
                                    <div className="text-sm font-semibold text-gray-700 break-words">
                                      Disabled
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Right side: Configure button - full width on mobile */}
                          <div className="flex-shrink-0 w-full sm:w-auto">
                            <Button
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePlatformSettings(platform);
                              }}
                              variant="outline"
                              className="w-full sm:w-auto gap-2 bg-white hover:bg-gray-50 border-gray-300 hover:border-gray-400"
                            >
                              <Settings className="h-4 w-4" />
                              Configure
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {autoCreateEnabledPlatforms.length === 0 && autoCreateDisabledPlatforms.length === 0 && !loading && (
          <Card className="relative overflow-hidden border-dashed border-2 border-gray-300 bg-gradient-to-br from-white via-blue-50/20 to-indigo-50/30">
            <div className="absolute inset-0 bg-grid-pattern opacity-5" />
            <CardContent className="relative flex flex-col items-center justify-center py-16 text-center">
              {/* Enhanced text content */}
              <div className="max-w-md">
                <h3 className="text-2xl font-bold bg-gradient-to-r from-gray-700 via-blue-700 to-indigo-700 bg-clip-text text-transparent mb-3">
                  No Platforms Connected
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  No social media or blog platforms are currently connected to this brand.
                </p>
              </div>
              
              {/* Decorative elements */}
              <div className="absolute top-8 right-8 w-16 h-16 bg-gradient-to-br from-blue-200/30 to-transparent rounded-full" />
              <div className="absolute bottom-8 left-8 w-12 h-12 bg-gradient-to-br from-indigo-200/30 to-transparent rounded-full" />
            </CardContent>
          </Card>
        )}
      </div>

    </div>
  );
};

export default ConnectionsTab;